import {
  NOTIFICATION_EVENT_DESCRIPTIONS,
  NOTIFICATION_PRODUCT_LABELS,
  NotificationEventKey,
  NotificationProductCode,
  type MailTemplate,
  type NotificationEventKey as NotificationEventKeyType,
  type NotificationProductCode as NotificationProductCodeType,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  MAIL_TEMPLATE_PRODUCT_TABS,
  applyMailTemplatePreview,
  partitionMailTemplates,
} from '@/platform/features/mail/mail-workspace-panel-helpers';
import {
  useMailPolicy,
  useMailTemplates,
  useResetMailTemplate,
  useSendMailTemplateTest,
  useUpdateMailPolicy,
  useUpdateMailTemplate,
} from '@/platform/features/mail/use-mail-workspace';

const RECIPIENT_DEPENDENT_BODY = new Set<NotificationEventKeyType>([
  NotificationEventKey.LEAVE_REQUEST_APPROVED,
  NotificationEventKey.FLEET_DAMAGE_CLOSED,
  NotificationEventKey.INTRANET_MENTIONED,
]);

type TemplateDraft = {
  subjectTemplate: string;
  headingTemplate: string;
  bodyParagraphsText: string;
  actionLabel: string;
  subjectTemplateDu: string;
  headingTemplateDu: string;
  bodyParagraphsTextDu: string;
  actionLabelDu: string;
};

type DraftSource = {
  eventKey: NotificationEventKeyType;
  updatedAt: string | null;
  isCustomized: boolean;
};

function toDraft(template: MailTemplate): TemplateDraft {
  return {
    subjectTemplate: template.subjectTemplate,
    headingTemplate: template.headingTemplate,
    bodyParagraphsText: template.bodyParagraphs.join('\n'),
    actionLabel: template.actionLabel ?? '',
    subjectTemplateDu: template.subjectTemplateDu ?? '',
    headingTemplateDu: template.headingTemplateDu ?? '',
    bodyParagraphsTextDu: (template.bodyParagraphsDu ?? []).join('\n'),
    actionLabelDu: template.actionLabelDu ?? '',
  };
}

/** Policy toggles and product template editor for `/verwaltung/e-mail`. */
export function MailWorkspacePanels() {
  const policyQuery = useMailPolicy();
  const templatesQuery = useMailTemplates();
  const updatePolicy = useUpdateMailPolicy();
  const updateTemplate = useUpdateMailTemplate();
  const resetTemplate = useResetMailTemplate();
  const sendTest = useSendMailTemplateTest();

  const [endUsers, setEndUsers] = useState(true);
  const [agents, setAgents] = useState(true);
  const [syncedPolicy, setSyncedPolicy] = useState(policyQuery.data);
  const [productCode, setProductCode] = useState<NotificationProductCodeType>(
    NotificationProductCode.HELPDESK,
  );
  const [selectedKey, setSelectedKey] = useState<NotificationEventKeyType | null>(null);
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [draftSource, setDraftSource] = useState<DraftSource | null>(null);

  if (policyQuery.data && policyQuery.data !== syncedPolicy) {
    setSyncedPolicy(policyQuery.data);
    setEndUsers(policyQuery.data.emailEnabledForEndUsers);
    setAgents(policyQuery.data.emailEnabledForAgents);
  }

  const items = useMemo(
    () => (templatesQuery.data?.items ?? []).filter((item) => item.productCode === productCode),
    [productCode, templatesQuery.data?.items],
  );
  const { emailOn, emailOff } = useMemo(() => partitionMailTemplates(items), [items]);
  const selected = items.find((item) => item.eventKey === selectedKey) ?? items[0] ?? null;
  const placeholders = useMemo(() => selected?.placeholders ?? [], [selected?.placeholders]);

  if (selected) {
    const nextSource: DraftSource = {
      eventKey: selected.eventKey,
      updatedAt: selected.updatedAt,
      isCustomized: selected.isCustomized,
    };
    if (
      draftSource?.eventKey !== nextSource.eventKey ||
      draftSource?.updatedAt !== nextSource.updatedAt ||
      draftSource?.isCustomized !== nextSource.isCustomized
    ) {
      setDraftSource(nextSource);
      setDraft(toDraft(selected));
    }
  } else if (draftSource) {
    setDraftSource(null);
    setDraft(null);
  }

  const preview = useMemo(() => {
    if (!draft || !selected) {
      return null;
    }
    const replace = (value: string) => applyMailTemplatePreview(value, placeholders);
    const paragraphs = draft.bodyParagraphsText
      .split('\n')
      .map((line) => replace(line.trim()))
      .map((line) => line.trim())
      .filter(Boolean);
    return {
      subject: replace(draft.subjectTemplate),
      heading: replace(draft.headingTemplate),
      paragraphs,
      actionLabel: draft.actionLabel.trim()
        ? replace(draft.actionLabel)
        : (selected.actionLabel ?? 'Öffnen'),
    };
  }, [draft, placeholders, selected]);

  async function onSavePolicy() {
    try {
      await updatePolicy.mutateAsync({
        emailEnabledForEndUsers: endUsers,
        emailEnabledForAgents: agents,
      });
      toast.success('Globale E-Mail-Politik gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Politik konnte nicht gespeichert werden.',
      );
    }
  }

  async function onSaveTemplate() {
    if (!selected || !draft) {
      return;
    }
    const bodyParagraphs = draft.bodyParagraphsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const bodyParagraphsDu = draft.bodyParagraphsTextDu
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    try {
      await updateTemplate.mutateAsync({
        eventKey: selected.eventKey,
        body: {
          subjectTemplate: draft.subjectTemplate.trim(),
          headingTemplate: draft.headingTemplate.trim(),
          bodyParagraphs,
          actionLabel: draft.actionLabel.trim() ? draft.actionLabel.trim() : null,
          subjectTemplateDu: draft.subjectTemplateDu.trim() ? draft.subjectTemplateDu.trim() : null,
          headingTemplateDu: draft.headingTemplateDu.trim() ? draft.headingTemplateDu.trim() : null,
          bodyParagraphsDu: bodyParagraphsDu.length > 0 ? bodyParagraphsDu : null,
          actionLabelDu: draft.actionLabelDu.trim() ? draft.actionLabelDu.trim() : null,
        },
      });
      toast.success('Vorlage gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Vorlage konnte nicht gespeichert werden.',
      );
    }
  }

  async function onResetTemplate() {
    if (!selected) {
      return;
    }
    try {
      await resetTemplate.mutateAsync(selected.eventKey);
      toast.success('Vorlage auf Standard zurückgesetzt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Zurücksetzen ist fehlgeschlagen.',
      );
    }
  }

  async function onTestMail() {
    if (!selected) {
      return;
    }
    try {
      const result = await sendTest.mutateAsync({ eventKey: selected.eventKey });
      toast.success(
        policyQuery.data?.smtpConfigured
          ? `Testmail an ${result.to} gesendet (${result.subject}).`
          : `Test vorbereitet für ${result.to} — ohne Versand nur Backend-Log.`,
      );
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Testmail fehlgeschlagen.');
    }
  }

  function renderEventButton(item: MailTemplate) {
    return (
      <button
        key={item.eventKey}
        type="button"
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted',
          selected?.eventKey === item.eventKey && 'bg-muted',
        )}
        onClick={() => setSelectedKey(item.eventKey)}
      >
        <span className="min-w-0 truncate">{item.label}</span>
        {item.isCustomized ? (
          <Badge variant="secondary" className="shrink-0">
            Angepasst
          </Badge>
        ) : null}
      </button>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Wer Mail bekommt</CardTitle>
          <CardDescription>
            Schaltet den E-Mail-Kanal organisationsweit. Die In-App-Glocke bleibt unabhängig davon
            aktiv. Der Passwort-Link hängt nicht an diesen Schaltern.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {policyQuery.isPending ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Politik wird geladen …
            </p>
          ) : null}
          {policyQuery.isError ? (
            <p className="text-sm text-muted-foreground">
              {policyQuery.error instanceof ApiRequestError
                ? policyQuery.error.message
                : 'Politik konnte nicht geladen werden.'}
            </p>
          ) : null}
          {policyQuery.data ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="mail-agents">E-Mail an IT-Rollen</Label>
                  <p className="text-xs text-muted-foreground">
                    Konten mit IT-Agent, IT-Leitung oder IT-Admin, in jedem Produkt.
                  </p>
                </div>
                <Switch id="mail-agents" checked={agents} onCheckedChange={setAgents} />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="mail-end-users">E-Mail an alle übrigen Konten</Label>
                  <p className="text-xs text-muted-foreground">
                    Alle übrigen Konten, in jedem Produkt.
                  </p>
                </div>
                <Switch id="mail-end-users" checked={endUsers} onCheckedChange={setEndUsers} />
              </div>
              <Button
                type="button"
                onClick={() => void onSavePolicy()}
                disabled={updatePolicy.isPending}
              >
                {updatePolicy.isPending ? (
                  <Loader2Icon className="animate-spin" aria-hidden />
                ) : null}
                Politik speichern
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Tabs
        value={productCode}
        onValueChange={(value) => {
          setProductCode(value as NotificationProductCodeType);
          setSelectedKey(null);
        }}
        className="space-y-3"
      >
        <TabsList
          aria-label="Produkt für E-Mail-Vorlagen"
          className="flex h-auto w-full flex-wrap justify-start"
        >
          {MAIL_TEMPLATE_PRODUCT_TABS.map((code) => (
            <TabsTrigger key={code} value={code}>
              {NOTIFICATION_PRODUCT_LABELS[code]}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-base">Vorlagen</CardTitle>
              <CardDescription>
                {NOTIFICATION_PRODUCT_LABELS[productCode]} — Ereignis wählen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-1 p-2">
              {templatesQuery.isPending ? (
                <p className="flex items-center gap-2 px-2 py-1 text-sm text-muted-foreground">
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Laden …
                </p>
              ) : null}
              {emailOn.map(renderEventButton)}
              {emailOff.length > 0 ? (
                <>
                  <p className="px-2 pt-3 pb-1 text-xs text-muted-foreground">
                    E-Mail ist hier standardmäßig aus. Die Vorlage gilt, sobald eine Person E-Mail
                    einschaltet.
                  </p>
                  {emailOff.map(renderEventButton)}
                </>
              ) : null}
              {productCode === NotificationProductCode.INTRANET ? (
                <p className="px-2 pt-3 text-xs text-muted-foreground">
                  Der Chat verschickt keine E-Mail.
                </p>
              ) : null}
              {productCode === NotificationProductCode.LEAVE ? (
                <p className="px-2 pt-3 text-xs text-muted-foreground">
                  Krankmeldungen bleiben in der Glocke.
                </p>
              ) : null}
            </CardContent>
          </Card>

          <div className="space-y-6">
            {selected && draft ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{selected.label}</CardTitle>
                    <CardDescription>
                      {NOTIFICATION_EVENT_DESCRIPTIONS[selected.eventKey]}
                      {selected.eventKey === NotificationEventKey.CSAT ||
                      selected.eventKey === NotificationEventKey.APPROVAL_REQUESTED
                        ? ' Sterne- bzw. Freigabe-Buttons bleiben fest im Code.'
                        : ''}
                      {RECIPIENT_DEPENDENT_BODY.has(selected.eventKey)
                        ? ' Der Produktsatz hängt vom Empfänger ab.'
                        : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="mail-subject">Betreff</Label>
                      <Input
                        id="mail-subject"
                        value={draft.subjectTemplate}
                        onChange={(event) =>
                          setDraft((current) =>
                            current ? { ...current, subjectTemplate: event.target.value } : current,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mail-heading">Überschrift</Label>
                      <Input
                        id="mail-heading"
                        value={draft.headingTemplate}
                        onChange={(event) =>
                          setDraft((current) =>
                            current ? { ...current, headingTemplate: event.target.value } : current,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mail-body">Textabsätze (eine Zeile = ein Absatz)</Label>
                      <Textarea
                        id="mail-body"
                        rows={6}
                        value={draft.bodyParagraphsText}
                        onChange={(event) =>
                          setDraft((current) =>
                            current
                              ? { ...current, bodyParagraphsText: event.target.value }
                              : current,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mail-subject-du">Betreff, persönliche Fassung</Label>
                      <Input
                        id="mail-subject-du"
                        value={draft.subjectTemplateDu}
                        onChange={(event) =>
                          setDraft((current) =>
                            current
                              ? { ...current, subjectTemplateDu: event.target.value }
                              : current,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mail-heading-du">Überschrift, persönliche Fassung</Label>
                      <Input
                        id="mail-heading-du"
                        value={draft.headingTemplateDu}
                        onChange={(event) =>
                          setDraft((current) =>
                            current
                              ? { ...current, headingTemplateDu: event.target.value }
                              : current,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mail-body-du">
                        Textabsätze, persönliche Fassung (leer = mitgelieferte Fassung)
                      </Label>
                      <Textarea
                        id="mail-body-du"
                        rows={4}
                        value={draft.bodyParagraphsTextDu}
                        onChange={(event) =>
                          setDraft((current) =>
                            current
                              ? { ...current, bodyParagraphsTextDu: event.target.value }
                              : current,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mail-action">Button-Label (optional)</Label>
                      <Input
                        id="mail-action"
                        value={draft.actionLabel}
                        onChange={(event) =>
                          setDraft((current) =>
                            current ? { ...current, actionLabel: event.target.value } : current,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Platzhalter</p>
                      <ul className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                        {placeholders.map((item) => (
                          <li key={item.token}>
                            <code className="text-foreground">{item.token}</code> — {item.label}
                            {item.sample ? (
                              <>
                                {' '}
                                <span className="text-muted-foreground/80">
                                  (Beispiel: {item.sample})
                                </span>
                              </>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        onClick={() => void onSaveTemplate()}
                        disabled={updateTemplate.isPending}
                      >
                        {updateTemplate.isPending ? (
                          <Loader2Icon className="animate-spin" aria-hidden />
                        ) : null}
                        Vorlage speichern
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => void onResetTemplate()}
                        disabled={resetTemplate.isPending || !selected.isCustomized}
                      >
                        Zurücksetzen
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => void onTestMail()}
                        disabled={sendTest.isPending}
                      >
                        {sendTest.isPending ? (
                          <Loader2Icon className="animate-spin" aria-hidden />
                        ) : null}
                        Testmail an mich
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {preview ? (
                  <Card className="overflow-hidden">
                    <CardHeader>
                      <CardTitle className="text-base">Vorschau</CardTitle>
                      <CardDescription>
                        Nur Text. Die Vorschau zeigt die Mail, nicht das Erscheinungsbild der App.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="bg-muted/40 p-4">
                      {/* Fixed light shell. It mirrors wrapEmailHtml and must not follow the app theme. */}
                      <div className="mx-auto max-w-xl overflow-hidden rounded-xl border bg-white shadow-sm">
                        <div className="bg-[#0f4c81] px-6 py-4 text-sm font-medium text-white">
                          Mitarbeiterportal
                        </div>
                        <div className="space-y-3 px-6 py-6 text-[15px] leading-relaxed text-[#1f2937]">
                          <p className="text-xs text-[#6b7280]">Betreff: {preview.subject}</p>
                          <h2 className="text-xl font-semibold text-[#0f4c81]">
                            {preview.heading}
                          </h2>
                          {preview.paragraphs.map((line, index) => (
                            <p key={`${index}-${line.slice(0, 24)}`}>{line}</p>
                          ))}
                          <p className="pt-2">
                            <span className="inline-block rounded-md bg-[#0f4c81] px-4 py-2 text-sm font-semibold text-white">
                              {preview.actionLabel}
                            </span>
                          </p>
                        </div>
                        <div className="bg-[#f9fafb] px-6 py-3 text-xs text-[#6b7280]">
                          Automatische Nachricht vom Mitarbeiterportal.
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </Tabs>
    </>
  );
}
